import { useState } from "react";
import { ArrowUpRight, MapPin, Mail, Building2, Send } from "lucide-react";
import { toast } from "sonner";
import { HeroFade, Reveal } from "../components/Motion";
import { EMAIL, ADDRESS_LINES } from "../constants";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Website contact form — ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Your mail app is opening with the message ready to send.");
    setForm({ name: "", email: "", message: "" });
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-900 placeholder:text-slate-400 transition-colors duration-300 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/20";

  return (
    <main data-testid="contact-page">
      <section className="relative overflow-hidden px-6 pt-36 md:px-10 md:pt-44">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl pb-20">
          <HeroFade delay={0.1}>
            <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
              Contact — EY Tech LLC
            </p>
          </HeroFade>
          <h1 data-testid="contact-heading" className="max-w-3xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-slate-900 md:text-7xl">
            <HeroFade delay={0.2}>Talk to</HeroFade>
            <HeroFade delay={0.35}>
              <em className="text-blue-600">a human.</em>
            </HeroFade>
          </h1>

          <div className="mt-20 grid grid-cols-1 gap-10 md:grid-cols-5">
            <Reveal className="md:col-span-3">
              <div className="h-full rounded-2xl bg-white p-8 shadow-soft md:p-12">
                <div className="mb-6 flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                  <Mail className="h-4 w-4" />
                  Email — fastest
                </div>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-link"
                  className="group inline-flex flex-wrap items-center gap-3 font-serif text-2xl font-medium tracking-tight text-slate-900 transition-colors duration-300 hover:text-blue-600 sm:text-3xl md:text-4xl"
                >
                  <span className="break-all">{EMAIL}</span>
                  <ArrowUpRight className="h-7 w-7 shrink-0 text-blue-600 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
                <p className="mt-8 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">
                  Paid for a report? Send your website address here and the clock
                  starts. Questions before you pay? Same address. A person reads
                  every message.
                </p>
              </div>
            </Reveal>

            <div className="flex flex-col gap-10 md:col-span-2">
              <Reveal delay={0.15}>
                <div className="rounded-2xl bg-white p-8 shadow-soft">
                  <div className="mb-4 flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                    <Building2 className="h-4 w-4" />
                    Company
                  </div>
                  <p data-testid="contact-company-name" className="font-serif text-2xl font-medium tracking-tight text-slate-900">
                    EY TECH LLC
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="rounded-2xl bg-white p-8 shadow-soft">
                  <div className="mb-4 flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                    <MapPin className="h-4 w-4" />
                    Registered Office
                  </div>
                  <address data-testid="contact-address" className="text-sm not-italic leading-loose text-slate-600 md:text-base">
                    {ADDRESS_LINES.slice(1).map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </address>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <Reveal>
          <div className="rounded-2xl bg-white p-8 shadow-lift md:p-12">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
              Message us
            </p>
            <h2 data-testid="contact-form-heading" className="font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
              Write to us directly.
            </h2>
            <form data-testid="contact-form" onSubmit={handleSubmit} className="mt-10 flex flex-col gap-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <input
                  data-testid="contact-form-name-input"
                  type="text"
                  required
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                />
                <input
                  data-testid="contact-form-email-input"
                  type="email"
                  required
                  placeholder="Your email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
              </div>
              <textarea
                data-testid="contact-form-message-input"
                required
                rows={6}
                placeholder="Your message — include your website address if this is about a report."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-y`}
              />
              <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  className="group flex items-center gap-2.5 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
                >
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  Send Message
                </button>
                <p className="text-xs text-slate-400">
                  Opens your mail app with the message ready — nothing is stored on this site.
                </p>
              </div>
            </form>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
